/**
 * NetworkEditConfirmation - Confirmación de operación de edición de red
 *
 * @version 1.0
 * @created 2026-03-03
 * @module core/spatial/components
 *
 * @description
 * Popover flotante que aparece para confirmar operaciones de edición de red canónica.
 * Similar a ElementPreview pero especializado para operaciones de red.
 *
 * @remarks
 * **Modos soportados**:
 * - create-branch: Confirmar creación de nodo + edge
 * - split-segment: Confirmar división de edge en 2
 * - delete-node: Confirmar eliminación de nodo + edges conectados
 *
 * **Flujo**:
 * 1. Usuario hace operación (click en nodo, click en edge, etc.)
 * 2. Aparece popover flotante sobre el punto de operación
 * 3. Muestra mensaje específico según el modo
 * 4. Usuario confirma (Enter) → se ejecuta operación
 * 5. Usuario cancela (Esc o Q) → se cancela operación
 */

import { useEffect, useRef } from 'react';
import { Check, X, GitBranch, Split, Trash2, Move, AlertTriangle } from 'lucide-react';
import { mapEngine } from '../../map-engine/MapEngineService';
import type { NetworkEditMode } from '../contexts/NetworkEditContext';
import type { DeletionImpact } from '../services/canonical-network-api.service';
import './network-edit-confirmation.css';

/**
 * Props del componente
 */
export interface NetworkEditConfirmationProps {
  /**
   * Modo de edición activo
   */
  mode: Exclude<NetworkEditMode, null>;

  /**
   * Coordenadas [lng, lat] donde se realizará la operación
   */
  coordinates: [number, number];

  /**
   * Metadata adicional para el mensaje
   */
  metadata?: {
    nodeCount?: number;
    edgeCount?: number;
    message?: string;
    impact?: DeletionImpact;
  };

  /**
   * Callback al confirmar operación
   */
  onConfirm: () => void;

  /**
   * Callback al cancelar
   */
  onCancel: () => void;
}

/**
 * Mensajes por modo de edición (FIX: Hallazgo #4 - agregado delete-edge)
 */
const MODE_MESSAGES: Record<Exclude<NetworkEditMode, null>, string> = {
  'create-branch': '¿Crear nodo y conectar?',
  'split-segment': '¿Dividir segmento aquí?',
  'delete-node': '¿Eliminar nodo?',
  'delete-edge': '¿Eliminar segmento?',
  'move-node': '¿Confirmar nueva posición?',
  'insert-node-on-edge': '¿Insertar nodo aquí?',
  'draw-freehand-segment': '¿Confirmar segmento libre?',
};

/**
 * Iconos por modo de edición (FIX: Hallazgo #4 - agregado delete-edge)
 */
const MODE_ICONS: Record<Exclude<NetworkEditMode, null>, React.ReactNode> = {
  'create-branch': <GitBranch size={16} />,
  'split-segment': <Split size={16} />,
  'delete-node': <Trash2 size={16} />,
  'delete-edge': <Trash2 size={16} />,
  'move-node': <Move size={16} />,
  'insert-node-on-edge': <Split size={16} />,
  'draw-freehand-segment': <Split size={16} />,
};

/**
 * Componente NetworkEditConfirmation
 */
export function NetworkEditConfirmation({
  mode,
  coordinates,
  metadata,
  onConfirm,
  onCancel,
}: NetworkEditConfirmationProps): JSX.Element {
  const popoverRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);

  /**
   * Calcula posición en píxeles a partir de coordenadas geográficas
   * y actualiza cuando el mapa se mueve
   */
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !popoverRef.current || !lineRef.current) return;

    const updatePosition = (): void => {
      if (!map || !popoverRef.current || !lineRef.current) return;

      // Convertir coordenadas geográficas a píxeles del mapa
      const point = map.project([coordinates[0], coordinates[1]]);

      const popoverWidth = popoverRef.current.offsetWidth;
      const popoverHeight = popoverRef.current.offsetHeight;

      // El mapa tiene un offset por el TopBar (120px * ui-scale)
      // Obtener el valor de --ui-scale
      const uiScale = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ui-scale')) || 1.0;
      const topBarHeight = 120 * uiScale;

      // Ajustar las coordenadas agregando el offset del topbar
      const adjustedY = point.y + topBarHeight;

      // Distancia total fija desde el bottom del popover hasta el punto de clic
      const totalGap = 80;

      // Posicionar popover: su bottom estará a totalGap px arriba del punto de clic
      popoverRef.current.style.left = `${point.x - popoverWidth / 2}px`;
      popoverRef.current.style.top = `${adjustedY - totalGap - popoverHeight}px`;

      // La línea llena todo el gap desde el bottom del popover hasta el punto de clic
      lineRef.current.style.left = `${point.x}px`;
      lineRef.current.style.top = `${adjustedY - totalGap}px`;
      lineRef.current.style.height = `${totalGap}px`;
    };

    // Posicionar inicialmente
    updatePosition();

    // Actualizar posición cuando el mapa se mueve o hace zoom
    map.on('move', updatePosition);
    map.on('zoom', updatePosition);

    return () => {
      map.off('move', updatePosition);
      map.off('zoom', updatePosition);
    };
  }, [coordinates]);

  /**
   * Maneja teclas Q/Esc (cancelar) y Enter (confirmar)
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'q' || e.key === 'Q' || e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      } else if (e.key === 'Enter') {
        if (metadata?.impact?.canDelete === false) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        onConfirm();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [metadata?.impact?.canDelete, onCancel, onConfirm]);

  /**
   * Click fuera del popover = cancelar
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onCancel();
      }
    };

    // Pequeño delay para no capturar el click que abrió el popover
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onCancel]);

  // Construir mensaje con metadata si está disponible
  const message = metadata?.message || MODE_MESSAGES[mode];

  // Construir submensajes según el modo y el impacto
  const submessages: string[] = [];

  // Mensaje de edges conectados para delete-node
  if (mode === 'delete-node' && metadata?.edgeCount !== undefined) {
    if (metadata.edgeCount === 0) {
      submessages.push('Este nodo no tiene edges conectados');
    } else {
      submessages.push(
        `Esto eliminará también ${metadata.edgeCount} edge${metadata.edgeCount > 1 ? 's' : ''} conectado${metadata.edgeCount > 1 ? 's' : ''}`
      );
    }
  }

  // Mensaje de impacto en asociaciones punto-red
  if ((mode === 'delete-edge' || mode === 'delete-node') && metadata?.impact) {
    const {
      totalAffected,
      willBlockDeletion,
      automaticAssociations,
      manualAssociations,
      repairableAutomatically,
      requiresManualResolution,
      canDeleteDirectly,
      canDeleteWithAutoRepair,
      canDelete,
      byEntityType,
    } = metadata.impact;
    if (totalAffected > 0) {
      // Construir lista de entidades afectadas
      const entityList = Object.entries(byEntityType)
        .map(([_, info]) => `${info.count} ${info.label}${info.count > 1 ? 's' : ''}`)
        .join(', ');

      submessages.push(`Entidades asociadas: ${entityList}`);

      if (canDeleteDirectly) {
        submessages.push('No requiere repair adicional para eliminar');
      } else if (canDeleteWithAutoRepair) {
        submessages.push(
          `Reasociación automática disponible para ${repairableAutomatically} asociación${repairableAutomatically !== 1 ? 'es' : ''}`,
        );
      } else if (!canDelete && willBlockDeletion > 0) {
        submessages.push(
          `Bloqueado: ${requiresManualResolution} asociación${requiresManualResolution !== 1 ? 'es requieren' : ' requiere'} resolución manual`,
        );
      }

      if (automaticAssociations > 0) {
        submessages.push(
          `${automaticAssociations} automática${automaticAssociations !== 1 ? 's' : ''}`,
        );
      }
      if (manualAssociations > 0) {
        submessages.push(
          `${manualAssociations} manual${manualAssociations !== 1 ? 'es protegidas' : ' protegida'}`,
        );
      }
    }
  }

  const submessage = submessages.length > 0 ? submessages.join(' | ') : null;
  const hasImpact = metadata?.impact && metadata.impact.totalAffected > 0;
  const deletionBlocked = metadata?.impact?.canDelete === false;
  const confirmLabel = hasImpact && metadata?.impact
    ? metadata.impact.canDeleteWithAutoRepair && !metadata.impact.canDeleteDirectly
      ? 'Reparar y eliminar'
      : 'Eliminar'
    : 'Confirmar';

  return (
    <>
      {/* Línea de conexión al punto de operación */}
      <div ref={lineRef} className="network-edit-confirmation-line" />

      <div
        ref={popoverRef}
        className={`network-edit-confirmation-popover${hasImpact ? ' has-impact' : ''}`}
      >
        {/* Punta que apunta hacia abajo */}
        <div className="network-edit-confirmation-arrow" />

        {/* Contenido */}
        <div className="network-edit-confirmation-content">
          {MODE_ICONS[mode]}
          <div className="network-edit-confirmation-text">
            <span className="network-edit-confirmation-question">{message}</span>
            {submessage && (
              <span className={`network-edit-confirmation-submessage${hasImpact ? ' impact-warning' : ''}`}>
                {hasImpact && <AlertTriangle size={12} className="impact-icon" />}
                {submessage}
              </span>
            )}
          </div>
        </div>

        {/* Acciones */}
        <div className="network-edit-confirmation-actions">
          <button
            className="network-edit-confirmation-btn network-edit-confirmation-btn-cancel"
            onClick={onCancel}
            title="Cancelar (Q / Esc)"
          >
            <X size={14} />
          </button>
          <button
            className={`network-edit-confirmation-btn ${
              mode === 'delete-node' || mode === 'delete-edge'
                ? 'network-edit-confirmation-btn-delete'
                : 'network-edit-confirmation-btn-confirm'
            }`}
            onClick={onConfirm}
            title={`${confirmLabel} (Enter)`}
            aria-label={confirmLabel}
            disabled={deletionBlocked}
          >
            <Check size={14} />
          </button>
        </div>
      </div>
    </>
  );
}
